import React, { useState, useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Input,
  Textarea,
  Button,
  Flex,
  Box,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  TextAlignLeft,
  CalendarBlank,
  Tag,
  Users,
  FileDoc,
  Check,
  CaretDown,
  Circle,
} from 'phosphor-react';
import { IDataNoPagination } from '../../types/shared';
import { MarkdownRenderer } from './MarkdownRenderer';
import {
  KanbanPrioridade,
  IKanbanUserOption,
  IKanbanVinculo,
  IKanbanCampoDefinicao,
  IKanbanCampoValorInput,
} from '../../hooks/useKanban';
import { UserPapelPicker } from './UserPapelPicker';
import { KanbanCustomFieldInput } from './KanbanCustomFieldInput';
import { KanbanDatePicker } from './KanbanDatePicker';
import { useEventos, IEventoCategoria, IEventoServico } from '../../hooks/useEventos';
import {
  IRelatorioAssinaturas,
  RELATORIO_PADRAO,
  SecaoRelatorio,
  relatorioParaCard,
} from '../../pages/eventos/components/contrato/RelatorioAssinaturas';

export interface IContratoEventoData {
  contrato_categoria_id?: number;
  contrato_servico_ids?: number[];
  contrato_valor?: number;
  contrato_dt_vencimento?: string;
  contrato_fornecedor_documento?: string;
  contrato_relatorio_emails?: string[];
  contrato_relatorio_dias?: number[];
  contrato_relatorio_hora?: string;
  contrato_relatorio_data?: string | null;
}

interface KanbanAddCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  columnId: number;
  columnTitle: string;
  departamentoIds?: number[];
  campos?: IKanbanCampoDefinicao[];
  isEventProject?: boolean;
  onSave: (
    // eslint-disable-next-line no-unused-vars
    titulo: string,
    // eslint-disable-next-line no-unused-vars
    descricao: string,
    // eslint-disable-next-line no-unused-vars
    userIds?: number[],
    // eslint-disable-next-line no-unused-vars
    prioridade?: KanbanPrioridade,
    // eslint-disable-next-line no-unused-vars
    expiraEm?: string,
    // eslint-disable-next-line no-unused-vars
    campoValores?: IKanbanCampoValorInput[],
    // eslint-disable-next-line no-unused-vars
    contratoData?: IContratoEventoData,
    // Vínculos com papel (responsavel|supervisor). `userIds` segue sendo os
    // responsáveis p/ compat; `vinculos` carrega responsáveis + supervisores.
    // eslint-disable-next-line no-unused-vars
    vinculos?: IKanbanVinculo[],
  ) => void;
}

export const KanbanAddCardModal: React.FC<KanbanAddCardModalProps> = ({
  isOpen,
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  columnId,
  columnTitle,
  departamentoIds,
  campos = [],
  isEventProject,
  onSave,
}) => {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState<KanbanPrioridade>('media');
  const [expiraEm, setExpiraEm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<IKanbanUserOption[]>([]);
  // Supervisores (papel 'supervisor') — opcional, N de cada (Task #496).
  const [selectedSupervisors, setSelectedSupervisors] = useState<IKanbanUserOption[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [campoValues, setCampoValues] = useState<Record<number, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const { getCategorias, getServicos } = useEventos();

  // A busca de usuários (debounce + query) agora vive no UserPapelPicker, que é
  // usado p/ Responsáveis e Supervisores. Aqui só guardamos a seleção.

  // ── Campos de contrato (evento) ──
  const [contratoCategoria, setContratoCategoria] = useState<number | null>(null);
  const [contratoServicos, setContratoServicos] = useState<number[]>([]);
  const [contratoValor, setContratoValor] = useState('');
  const [contratoDtVencimento, setContratoDtVencimento] = useState('');
  const [contratoCnpj, setContratoCnpj] = useState('');
  const [relatorio, setRelatorio] = useState<IRelatorioAssinaturas>(RELATORIO_PADRAO);

  const { data: categoriasData } = useQuery<IDataNoPagination<IEventoCategoria[]>>({
    queryKey: ['evento-categorias'],
    queryFn: getCategorias,
    enabled: isOpen && !!isEventProject,
  });
  const categorias = categoriasData?.data || [];

  const { data: servicosData } = useQuery<IDataNoPagination<IEventoServico[]>>({
    queryKey: ['evento-servicos'],
    queryFn: getServicos,
    enabled: isOpen && !!isEventProject,
  });
  const todosServicos = servicosData?.data || [];
  const servicosFiltrados = todosServicos.filter(
    s => !contratoCategoria || s.categoria_id === contratoCategoria,
  );

  useEffect(() => {
    if (!isOpen) {
      setTitulo('');
      setDescricao('');
      setPrioridade('media');
      setExpiraEm('');
      setSelectedUsers([]);
      setSelectedSupervisors([]);
      setTabIndex(0);
      setCampoValues({});
      setIsSaving(false);
      setContratoCategoria(null);
      setContratoServicos([]);
      setContratoValor('');
      setContratoDtVencimento('');
      setContratoCnpj('');
      setRelatorio(RELATORIO_PADRAO);
    }
  }, [isOpen]);

  const buildCampoValores = (): IKanbanCampoValorInput[] =>
    Object.entries(campoValues)
      .filter(([, val]) => val !== null && val !== undefined && val !== '')
      .map(([campoIdStr, val]) => {
        const campoId = parseInt(campoIdStr, 10);
        const campo = campos.find(c => c.id === campoId);
        const input: IKanbanCampoValorInput = { campo_id: campoId };
        if (!campo) return input;
        if (campo.tipo === 'texto' || campo.tipo === 'select') input.valor_texto = String(val);
        else if (campo.tipo === 'numero' || campo.tipo === 'moeda')
          input.valor_numero = Number(val);
        else if (campo.tipo === 'data') input.valor_data = String(val);
        else if (campo.tipo === 'checkbox') input.valor_boolean = Boolean(val);
        return input;
      });

  const handleSave = () => {
    if (titulo.trim() && selectedUsers.length > 0 && !isSaving) {
      setIsSaving(true);
      const userIds = selectedUsers.map(u => u.id).filter((id): id is number => id !== undefined);
      // Vínculos com papel: responsáveis + supervisores. O backend aceita
      // `vinculos`; `userIds` segue p/ compat (responsáveis).
      const vinculos: IKanbanVinculo[] = [
        ...selectedUsers
          .filter(u => u.id !== undefined)
          .map(u => ({ user_id: u.id as number, papel: 'responsavel' as const })),
        ...selectedSupervisors
          .filter(u => u.id !== undefined)
          .map(u => ({ user_id: u.id as number, papel: 'supervisor' as const })),
      ];
      const campoValores = buildCampoValores();
      const contratoData: IContratoEventoData | undefined = isEventProject
        ? {
            contrato_categoria_id: contratoCategoria || undefined,
            contrato_servico_ids: contratoServicos.length > 0 ? contratoServicos : undefined,
            contrato_valor: parseFloat(contratoValor.replace(/\./g, '').replace(',', '.')) || 0,
            contrato_dt_vencimento: contratoDtVencimento || undefined,
            contrato_fornecedor_documento: contratoCnpj.replace(/\D/g, '') || undefined,
            ...relatorioParaCard(relatorio),
          }
        : undefined;
      onSave(
        titulo.trim(),
        descricao,
        userIds,
        prioridade,
        expiraEm || undefined,
        campoValores.length > 0 ? campoValores : undefined,
        contratoData,
        vinculos,
      );
    }
  };

  const toggleUser = (user: IKanbanUserOption) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    else setSelectedUsers([...selectedUsers, user]);
  };

  // Toggle de supervisor. Um usuário não pode ser responsável E supervisor ao
  // mesmo tempo no card — ao marcar supervisor, removo dos responsáveis.
  const toggleSupervisor = (user: IKanbanUserOption) => {
    const isSelected = selectedSupervisors.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedSupervisors(selectedSupervisors.filter(u => u.id !== user.id));
    } else {
      setSelectedSupervisors([...selectedSupervisors, user]);
      setSelectedUsers(prev => prev.filter(u => u.id !== user.id));
    }
  };

  const camposObrigatoriosPreenchidos = campos
    .filter(c => c.obrigatorio)
    .every(c => {
      const val = campoValues[c.id];
      if (val === null || val === undefined || val === '') return false;
      if (c.tipo === 'checkbox') return true;
      return true;
    });

  const cnpjLimpo = contratoCnpj.replace(/\D/g, '');
  const valorNum = parseFloat((contratoValor || '0').replace(/\./g, '').replace(',', '.'));
  const contratoPreenchido =
    !isEventProject ||
    (!!contratoCategoria && !Number.isNaN(valorNum) && valorNum > 0 && cnpjLimpo.length >= 11);

  const canSave =
    titulo.trim() &&
    selectedUsers.length > 0 &&
    camposObrigatoriosPreenchidos &&
    contratoPreenchido &&
    !(isEventProject && relatorio.invalido);

  const prioridadeConfig: Record<KanbanPrioridade, { label: string; color: string }> = {
    alta: { label: 'Alta', color: '#E53E3E' },
    media: { label: 'Media', color: '#ED8936' },
    baixa: { label: 'Baixa', color: '#48BB78' },
  };

  const getMissingMessage = () => {
    if (!titulo.trim()) return 'Preencha o titulo';
    if (selectedUsers.length === 0) return 'Selecione um responsavel';
    if (!camposObrigatoriosPreenchidos) return 'Preencha os campos obrigatorios';
    if (!contratoPreenchido) return 'Preencha categoria, valor e CNPJ/CPF';
    if (isEventProject && relatorio.invalido) return 'Corrija ou apague o e-mail do relatório';
    return '';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent borderRadius="2xl" mx={4} overflow="hidden" maxH="90vh">
        <ModalCloseButton top={3} right={3} borderRadius="full" zIndex={2} size="sm" />

        <ModalBody p={0} display="flex" flexDirection={{ base: 'column', md: 'row' }}>
          {/* Coluna principal */}
          <Box flex={1} p={6} overflowY="auto" maxH="90vh">
            {/* Header */}
            <Box mb={5}>
              <Text fontSize="11px" color="gray.400" fontWeight="500" mb={0.5}>
                Adicionar em {columnTitle}
              </Text>
              <Input
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                placeholder="Titulo do card..."
                variant="flushed"
                fontSize="xl"
                fontWeight="700"
                color="gray.900"
                borderColor="gray.200"
                _focus={{ borderColor: 'blue.400' }}
                _placeholder={{ color: 'gray.300' }}
                autoFocus
              />
            </Box>

            {/* Descricao */}
            <Box mb={5}>
              <Flex alignItems="center" gap={1.5} mb={2}>
                <TextAlignLeft size={14} color="#718096" weight="bold" />
                <Text
                  fontSize="12px"
                  fontWeight="600"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Descricao
                </Text>
              </Flex>
              <Tabs index={tabIndex} onChange={setTabIndex} size="sm" variant="soft-rounded">
                <TabList mb={2}>
                  <Tab
                    fontSize="12px"
                    px={3}
                    py={1}
                    _selected={{ bg: 'gray.100', color: 'gray.800' }}
                  >
                    Escrever
                  </Tab>
                  <Tab
                    fontSize="12px"
                    px={3}
                    py={1}
                    _selected={{ bg: 'gray.100', color: 'gray.800' }}
                  >
                    Preview
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel p={0}>
                    <Textarea
                      value={descricao}
                      onChange={e => setDescricao(e.target.value)}
                      placeholder="Descreva o card usando Markdown..."
                      minH="120px"
                      borderRadius="xl"
                      fontSize="13px"
                      _focus={{
                        borderColor: 'blue.400',
                        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.1)',
                      }}
                    />
                  </TabPanel>
                  <TabPanel p={0}>
                    <Box
                      minH="120px"
                      p={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="xl"
                      bg="gray.50"
                    >
                      {descricao ? (
                        <MarkdownRenderer content={descricao} />
                      ) : (
                        <Text color="gray.300" fontSize="13px">
                          Nenhum conteudo
                        </Text>
                      )}
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>

            <Divider mb={5} />

            {/* Responsaveis (obrigatorio) + Supervisores (opcional) */}
            <Box>
              <Flex alignItems="center" gap={1.5} mb={3}>
                <Users size={14} color="#718096" weight="bold" />
                <Text
                  fontSize="12px"
                  fontWeight="600"
                  color="gray.500"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Pessoas
                </Text>
              </Flex>

              <UserPapelPicker
                label="Responsaveis"
                papel="responsavel"
                selected={selectedUsers}
                onToggle={toggleUser}
                obrigatorio
                departamentoIds={departamentoIds}
              />

              <Box mt={4}>
                <UserPapelPicker
                  label="Supervisores"
                  papel="supervisor"
                  selected={selectedSupervisors}
                  onToggle={toggleSupervisor}
                  departamentoIds={departamentoIds}
                />
              </Box>
            </Box>
          </Box>

          {/* Sidebar */}
          <Box
            w={{ base: '100%', md: '260px' }}
            bg="gray.50"
            pt={10}
            px={5}
            pb={5}
            flexShrink={0}
            display="flex"
            flexDirection="column"
            overflowY="auto"
            maxH="90vh"
          >
            {/* Prioridade */}
            <Box mb={4}>
              <Text
                fontSize="11px"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={1.5}
              >
                Prioridade
              </Text>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  fontSize="13px"
                  bg="white"
                  fontWeight="500"
                  w="100%"
                  textAlign="left"
                  rightIcon={<CaretDown size={12} />}
                  _hover={{ bg: 'gray.50' }}
                  _active={{ bg: 'gray.50' }}
                >
                  <Flex align="center" gap={2}>
                    <Circle size={8} weight="fill" color={prioridadeConfig[prioridade].color} />
                    {prioridadeConfig[prioridade].label}
                  </Flex>
                </MenuButton>
                <MenuList
                  borderRadius="xl"
                  boxShadow="lg"
                  border="1px solid"
                  borderColor="gray.100"
                  py={1}
                  minW="140px"
                  fontSize="13px"
                >
                  <MenuItem
                    onClick={() => setPrioridade('alta')}
                    bg={prioridade === 'alta' ? 'red.50' : 'transparent'}
                    _hover={{ bg: 'red.50' }}
                    borderRadius="md"
                    mx={1}
                    px={3}
                    py={1.5}
                  >
                    <Flex align="center" gap={2}>
                      <Circle size={8} weight="fill" color="#E53E3E" />
                      <Text fontSize="13px" fontWeight="500">
                        Alta
                      </Text>
                    </Flex>
                  </MenuItem>
                  <MenuItem
                    onClick={() => setPrioridade('media')}
                    bg={prioridade === 'media' ? 'orange.50' : 'transparent'}
                    _hover={{ bg: 'orange.50' }}
                    borderRadius="md"
                    mx={1}
                    px={3}
                    py={1.5}
                  >
                    <Flex align="center" gap={2}>
                      <Circle size={8} weight="fill" color="#ED8936" />
                      <Text fontSize="13px" fontWeight="500">
                        Media
                      </Text>
                    </Flex>
                  </MenuItem>
                  <MenuItem
                    onClick={() => setPrioridade('baixa')}
                    bg={prioridade === 'baixa' ? 'green.50' : 'transparent'}
                    _hover={{ bg: 'green.50' }}
                    borderRadius="md"
                    mx={1}
                    px={3}
                    py={1.5}
                  >
                    <Flex align="center" gap={2}>
                      <Circle size={8} weight="fill" color="#48BB78" />
                      <Text fontSize="13px" fontWeight="500">
                        Baixa
                      </Text>
                    </Flex>
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>

            {/* Expiracao */}
            <Box mb={4}>
              <Flex alignItems="center" gap={1} mb={1.5}>
                <CalendarBlank size={12} color="#A0AEC0" weight="bold" />
                <Text
                  fontSize="11px"
                  fontWeight="600"
                  color="gray.400"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  Expiracao
                </Text>
              </Flex>
              <KanbanDatePicker value={expiraEm} onChange={setExpiraEm} />
            </Box>

            {/* Campos personalizaveis */}
            {campos.length > 0 && (
              <>
                <Divider my={3} />
                <Box mb={4}>
                  <Flex alignItems="center" gap={1} mb={2}>
                    <Tag size={12} color="#A0AEC0" weight="bold" />
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Campos
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={2.5}>
                    {campos.map(campo => (
                      <KanbanCustomFieldInput
                        key={campo.id}
                        campo={campo}
                        value={campoValues[campo.id] ?? null}
                        onChange={value => setCampoValues(prev => ({ ...prev, [campo.id]: value }))}
                        size="sm"
                      />
                    ))}
                  </Flex>
                </Box>
              </>
            )}

            {/* Campos do contrato (evento) */}
            {isEventProject && (
              <>
                <Divider my={3} />
                <Box mb={4}>
                  <Flex alignItems="center" gap={1} mb={2}>
                    <FileDoc size={12} color="#A0AEC0" weight="bold" />
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color="gray.400"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Contrato
                    </Text>
                  </Flex>
                  <Flex direction="column" gap={2.5}>
                    {/* CNPJ/CPF do fornecedor */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        CNPJ/CPF do contratado{' '}
                        <Text as="span" color="red.400">
                          *
                        </Text>
                      </Text>
                      <Input
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        bg="white"
                        value={contratoCnpj}
                        onChange={e => {
                          const raw = e.target.value.replace(/\D/g, '');
                          let formatted = raw;
                          if (raw.length <= 11) {
                            formatted = raw
                              .replace(/(\d{3})(\d)/, '$1.$2')
                              .replace(/(\d{3})(\d)/, '$1.$2')
                              .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                          } else {
                            formatted = raw
                              .replace(/^(\d{2})(\d)/, '$1.$2')
                              .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
                              .replace(/\.(\d{3})(\d)/, '.$1/$2')
                              .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
                          }
                          setContratoCnpj(formatted);
                        }}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                      />
                    </Box>

                    {/* Categoria */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Categoria{' '}
                        <Text as="span" color="red.400">
                          *
                        </Text>
                      </Text>
                      <Box
                        as="select"
                        w="100%"
                        fontSize="12px"
                        px={2}
                        py={1.5}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                        bg="white"
                        _focus={{ borderColor: 'blue.400', outline: 'none' }}
                        value={contratoCategoria ?? ''}
                        onChange={(e: any) => {
                          const val = e.target.value ? Number(e.target.value) : null;
                          setContratoCategoria(val);
                          setContratoServicos([]);
                        }}
                      >
                        <option value="">Selecione...</option>
                        {categorias.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.nome}
                          </option>
                        ))}
                      </Box>
                    </Box>

                    {/* Servicos */}
                    {contratoCategoria && servicosFiltrados.length > 0 && (
                      <Box>
                        <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                          Servicos
                        </Text>
                        <Box
                          maxH="100px"
                          overflowY="auto"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          bg="white"
                          p={1}
                        >
                          {servicosFiltrados.map(s => {
                            const checked = contratoServicos.includes(s.id);
                            return (
                              <Flex
                                key={s.id}
                                alignItems="center"
                                gap={2}
                                px={2}
                                py={1}
                                borderRadius="sm"
                                cursor="pointer"
                                bg={checked ? 'blue.50' : 'transparent'}
                                _hover={{ bg: checked ? 'blue.50' : 'gray.50' }}
                                onClick={() =>
                                  setContratoServicos(prev =>
                                    checked ? prev.filter(id => id !== s.id) : [...prev, s.id],
                                  )
                                }
                              >
                                <Box
                                  w="13px"
                                  h="13px"
                                  borderRadius="sm"
                                  border="2px solid"
                                  borderColor={checked ? 'blue.500' : 'gray.300'}
                                  bg={checked ? 'blue.500' : 'white'}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  flexShrink={0}
                                >
                                  {checked && <Check size={8} color="white" weight="bold" />}
                                </Box>
                                <Text fontSize="11px" color="gray.600" noOfLines={1}>
                                  {s.nome}
                                </Text>
                              </Flex>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Valor */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Valor Orçado (R$){' '}
                        <Text as="span" color="red.400">
                          *
                        </Text>
                      </Text>
                      <Input
                        as={NumericFormat}
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        bg="white"
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="R$ "
                        value={contratoValor ? parseFloat(contratoValor) : ''}
                        onValueChange={({ floatValue }: any) =>
                          setContratoValor(String(floatValue ?? ''))
                        }
                        placeholder="R$ 0,00"
                      />
                    </Box>

                    {/* Data do contrato */}
                    <Box>
                      <Text fontSize="10px" color="gray.400" fontWeight="600" mb={0.5}>
                        Data do contrato
                      </Text>
                      <Input
                        type="date"
                        size="sm"
                        fontSize="12px"
                        borderRadius="md"
                        bg="white"
                        value={contratoDtVencimento}
                        onChange={e => setContratoDtVencimento(e.target.value)}
                      />
                    </Box>
                  </Flex>
                </Box>
                <Divider my={3} />
                <Box mb={4}>
                  <SecaoRelatorio value={relatorio} onChange={setRelatorio} />
                </Box>
              </>
            )}

            {/* Spacer + Botao */}
            <Box flex={1} />
            <Button
              bg={canSave && !isSaving ? 'blue.500' : 'gray.200'}
              color={canSave && !isSaving ? 'white' : 'gray.400'}
              w="100%"
              size="md"
              borderRadius="xl"
              fontSize="14px"
              fontWeight="600"
              _hover={canSave && !isSaving ? { bg: 'blue.600' } : {}}
              _active={canSave && !isSaving ? { transform: 'scale(0.98)' } : {}}
              cursor={canSave && !isSaving ? 'pointer' : 'not-allowed'}
              onClick={handleSave}
              isDisabled={!canSave || isSaving}
              isLoading={isSaving}
              loadingText="Criando..."
            >
              Criar card
            </Button>
            {!canSave && (
              <Text fontSize="10px" color="gray.400" textAlign="center" mt={1.5}>
                {getMissingMessage()}
              </Text>
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
